import { api } from "@/lib/apiClient";
import { aufschlagFromEkVk, roundMoney, vkFromEk } from "@/pages/dokumente/documentPricing";

export type ZuschlagModus = "regel" | "manuell_prozent" | "manuell_vk";

export interface ZuschlagRegel {
  id?: number;
  ab_ek_netto: number;
  zuschlag_prozent: number;
  sort_index?: number;
}

export interface ZuschlagStaffelRow {
  id: number;
  ab_ek_netto: number;
  zuschlag_prozent: number;
  sort_index: number;
  vk: number | null;
  is_match: boolean;
}

export interface ArtikelZuschlagState {
  modus: ZuschlagModus | null;
  regel_id: number | null;
  manuell_prozent: number | null;
  matched_regel_id: number | null;
  staffeln: ZuschlagStaffelRow[];
  ek_netto: number | null;
  vk: number | null;
  aufschlag_prozent: number | null;
}

export async function getZuschlagRegeln(): Promise<ZuschlagRegel[]> {
  const data = await api.get<{ regeln: ZuschlagRegel[] }>("/artikel/einstellungen/zuschlag-regeln");
  return Array.isArray(data?.regeln) ? data.regeln : [];
}

export async function saveZuschlagRegeln(regeln: ZuschlagRegel[]): Promise<ZuschlagRegel[]> {
  const data = await api.put<{ regeln: ZuschlagRegel[] }>("/artikel/einstellungen/zuschlag-regeln", {
    regeln: regeln.map((r, index) => ({
      ab_ek_netto: roundMoney(Number(r.ab_ek_netto) || 0),
      zuschlag_prozent: roundMoney(Number(r.zuschlag_prozent) || 0),
      sort_index: r.sort_index ?? index,
    })),
  });
  return Array.isArray(data?.regeln) ? data.regeln : [];
}

export async function saveArtikelZuschlag(
  artikelId: number,
  body: {
    modus: ZuschlagModus;
    regel_id?: number | null;
    manuell_prozent?: number | null;
    verkaufspreis?: number | null;
  },
): Promise<{ ok: boolean; modus: ZuschlagModus; regel_id: number | null; verkaufspreis: number; aufschlag: number }> {
  return api.put(`/artikel/${artikelId}/zuschlag`, body);
}

export function matchZuschlagRegel(
  ek: number | null | undefined,
  rules: ZuschlagRegel[],
): ZuschlagRegel | null {
  if (ek == null || ek <= 0 || !rules.length) return null;
  let matched: ZuschlagRegel | null = null;
  for (const rule of rules) {
    const ab = Number(rule.ab_ek_netto) || 0;
    if (ek + 1e-9 >= ab) {
      if (!matched || ab >= (Number(matched.ab_ek_netto) || 0)) matched = rule;
    }
  }
  return matched;
}

/** Staffel, deren VK Cent-genau dem Ziel-VK entspricht (VK bleibt unverändert). */
export function findStaffelForVk(
  ek: number | null | undefined,
  vk: number | null | undefined,
  staffeln: ZuschlagStaffelRow[],
): ZuschlagStaffelRow | null {
  if (ek == null || ek <= 0 || vk == null || !staffeln.length) return null;
  const target = roundMoney(vk);
  const prefer = staffeln.find((s) => s.is_match);
  if (prefer) {
    const preferVk =
      prefer.vk != null ? roundMoney(prefer.vk) : roundMoney(vkFromEk(ek, prefer.zuschlag_prozent));
    if (preferVk === target) return prefer;
  }
  for (const row of staffeln) {
    const rowVk =
      row.vk != null ? roundMoney(row.vk) : roundMoney(vkFromEk(ek, row.zuschlag_prozent));
    if (rowVk === target) return row;
  }
  return null;
}

export function formatMoneyDe(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

export function staffelLabel(rule: Pick<ZuschlagRegel, "ab_ek_netto" | "zuschlag_prozent">): string {
  return `ab ${formatMoneyDe(rule.ab_ek_netto)} € · ${formatMoneyDe(rule.zuschlag_prozent)} %`;
}

export function zuschlagStateFromRegeln(
  regeln: ZuschlagRegel[],
  ek: number | null = null,
): ArtikelZuschlagState {
  const staffeln: ZuschlagStaffelRow[] = regeln
    .filter((row): row is ZuschlagRegel & { id: number } => row.id != null)
    .map((row) => ({
      id: row.id,
      ab_ek_netto: row.ab_ek_netto,
      zuschlag_prozent: row.zuschlag_prozent,
      sort_index: row.sort_index ?? 0,
      vk: null,
      is_match: false,
    }));
  const matched = matchZuschlagRegel(ek, regeln);
  return {
    modus: null,
    regel_id: null,
    manuell_prozent: null,
    matched_regel_id: matched?.id ?? null,
    staffeln,
    ek_netto: ek,
    vk: null,
    aufschlag_prozent: null,
  };
}

export { aufschlagFromEkVk, roundMoney, vkFromEk };
